import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, bulkUnSuspendUsers } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface RestoreUsersProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function RestoreUsersDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  changeLogRoles,
}: RestoreUsersProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isRestoring } = useMutation({
    mutationFn: () =>
      bulkUnSuspendUsers('User', selectedUsers, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to restore the user(s) has started.',
      });
      selectedUsers.forEach((user) => {
        queryClient.invalidateQueries({ queryKey: [UserKeys.User, user] });
      });
      setChangeLogMessage('');
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue restoring the selected user(s). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_restore_users, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.BulkRestoreUsers.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={selectedUsers.length > 1 ? 'Restore Users' : 'Restore User'}
      dialogContent={
        <Box>
          {selectedUsers.length > 1 ? (
            <Typography>
              Restore these <strong>{selectedUsers.length}</strong> users to
              active status.
            </Typography>
          ) : (
            <Typography>
              Restore <strong>{selectedUsers[0]}</strong> to active status.
            </Typography>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isRestoring}
          />
        </Box>
      }
      primaryButton={{
        loading: isRestoring,
        action: handleSubmit,
        disabled: isRestoring,
        title: selectedUsers.length > 1 ? 'Restore Users' : 'Restore User',
        color: 'primary',
      }}
    />
  );
}
